import React, { useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useForm, useFormState } from "react-final-form";
import UploadFile from "../../../../../components/UploadFile/UploadFile";
import { IFile } from "../../../../../components/UploadFile/types";
import { NotifyMode } from "../../../../notify/NotifyModeEnum";
import { mutateErrors } from "../../../../../utils";
import { useNotify } from "../../../../notify/useNotify";
import useProgramsQueries from "../../../hooks/useProgramQueriesHigher";

const useStyles = makeStyles(() => ({
  fileUploader: {
    marginTop: 16,
  },
}));

export function DetailImages({ name }: { name: string }) {
  const classes = useStyles();
  const { values } = useFormState();
  const {
    mutators: { push, remove },
  } = useForm();
  const snackNotify = useNotify();
  const { uploadDetailFile } = useProgramsQueries();

  const namePrefix = name.split(".")[2];

  const detailFiles: IFile[] = useMemo(
    () => values.step4?.majors?.[namePrefix]?.detailFiles || [],
    [namePrefix, values],
  );

  const addNewFiles = useCallback(
    async (files) => {
      const succeededFileNames: string[] = [];
      const errorMessages: string[] = [];

      for (const file of files) {
        try {
          let { data } = await uploadDetailFile(file);
          push(`${name}.detailFiles`, data);
          succeededFileNames.push(file.name);
        } catch (e) {
          const { message } = mutateErrors(e);
          errorMessages.push(`${file.name} - ${message}`);
        }
      }

      if (errorMessages.length > 0) {
        snackNotify("Error", errorMessages.join("\n"), NotifyMode.ERROR);
        return;
      }

      const successMessageEnding =
        succeededFileNames.length > 1
          ? "\nfiles have been uploaded."
          : " has been uploaded.";

      snackNotify(
        "Success",
        succeededFileNames.join("\n") + successMessageEnding,
        NotifyMode.SUCCESS,
      );
    },
    [name],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const removeFileHandler = useCallback(
    (file) => {
      const index = detailFiles.indexOf(file);

      remove(`${name}.detailFiles`, index);
    },
    [detailFiles, name, remove],
  );

  return (
    <div className={classes.fileUploader}>
      <UploadFile
        uploadedFiles={detailFiles}
        addNewFiles={addNewFiles}
        removeFile={removeFileHandler}
        titleText="Upload Documents/Flyers"
        allowedFileTypes={["pdf", "jpg", "jpeg", "png"]}
        maxSize={5}
        maxFiles={5}
      />
    </div>
  );
}
